import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatters';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';

export interface PlacaFinanceiroModalProps {
  open: boolean;
  onClose: () => void;
  placa: string;
  idEquipamento?: number;
  companyId?: string;
  modeloLabel?: string;
  empresa?: string;
}

interface PlacaFinanceiroPayload {
  placa: string;
  idEquipamento: number | null;
  receitas: Array<{
    idLocacao: string;
    cliente: string;
    produto: string;
    titulo: string;
    descricao: string;
    valor: number;
    valorPago: number;
    status: string;
    vencimento: string | null;
    ultimoPagamento: string | null;
  }>;
  despesas: Array<{
    origem: string;
    referencia: string;
    descricao: string;
    dataRef: string | null;
    valor: number;
  }>;
  totais: { faturado: number; custos: number; custoCompra?: number; diferenca: number };
  custosFonte?: 'detalhe' | 'agregado' | 'vazio';
  querySql?: string;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export const DiretoriaPlacaFinanceiroModal: React.FC<PlacaFinanceiroModalProps> = ({
  open,
  onClose,
  placa,
  idEquipamento,
  companyId,
  modeloLabel,
  empresa,
}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['diretoria-placa-financeiro', placa, idEquipamento, companyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('placa', placa);
      if (companyId) params.set('companyId', companyId);
      if (idEquipamento != null && idEquipamento > 0) {
        params.set('idEquipamento', String(idEquipamento));
      }
      const res = await api.get(`/reports/diretoria/placa-financeiro?${params.toString()}`);
      return res.data.data as PlacaFinanceiroPayload;
    },
    enabled: open && Boolean(placa?.trim()),
    staleTime: 60_000,
    retry: 1,
  });

  if (!open) return null;

  const totais = data?.totais;
  const errMsg =
    isError && error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: string }).message ?? 'Falha ao carregar')
      : isError
        ? 'Falha ao carregar detalhe da placa'
        : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Fechar" onClick={onClose} />
      <Card className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden border-border shadow-lg">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 border-b border-border/80 pb-3">
          <div className="min-w-0">
            <CardTitle className="text-lg">Placa {placa}</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {[modeloLabel, empresa].filter(Boolean).join(' · ') || 'Raio-x ERP (receita, custos e compra)'}
              {data?.idEquipamento != null ? ` · equip. #${data.idEquipamento}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DiretoriaSqlButton
              sql={data?.querySql}
              source="cobranca"
              title={`Placa ${placa} — SQL`}
            />
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 space-y-4 overflow-y-auto p-4">
          {isPending ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Carregando receitas e despesas…</p>
          ) : errMsg ? (
            <p className="py-10 text-center text-sm text-rose-700">{errMsg}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <div className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <p className="text-[10px] uppercase text-muted-foreground">Receita</p>
                  <p className="font-heading text-lg font-bold tabular-nums">
                    {formatCurrency(totais?.faturado ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <p className="text-[10px] uppercase text-muted-foreground">Custos</p>
                  <p className="font-heading text-lg font-bold tabular-nums">
                    {formatCurrency(totais?.custos ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <p className="text-[10px] uppercase text-muted-foreground">Custo compra</p>
                  <p className="font-heading text-lg font-bold tabular-nums">
                    {formatCurrency(totais?.custoCompra ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <p className="text-[10px] uppercase text-muted-foreground">Diferença</p>
                  <p
                    className={`font-heading text-lg font-bold tabular-nums ${
                      (totais?.diferenca ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'
                    }`}
                  >
                    {formatCurrency(totais?.diferenca ?? 0)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  Custos / despesas ({data?.despesas.length ?? 0})
                </h3>
                <p className="mb-2 text-[11px] text-muted-foreground">
                  {data?.custosFonte === 'agregado'
                    ? 'Custos alinhados ao ranking (OS + contas a pagar). A linha COMPRA é o custo de compra e entra só na Diferença.'
                    : 'O.S. + contas a pagar = Custos. A linha COMPRA é o custo de compra e entra só na Diferença.'}
                </p>
                {(data?.despesas.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {data?.idEquipamento
                      ? 'Nenhuma despesa ERP encontrada para o equipamento.'
                      : 'Equipamento ERP não identificado — despesas indisponíveis.'}
                  </p>
                ) : (
                  <div className="max-h-64 overflow-auto rounded-md border border-border/80">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 z-[1]">
                        <tr className="border-b border-border bg-muted/80 text-left text-muted-foreground backdrop-blur-sm">
                          <th className="px-2 py-1.5 font-medium">Origem</th>
                          <th className="px-2 py-1.5 font-medium">Descrição</th>
                          <th className="px-2 py-1.5 font-medium">Ref.</th>
                          <th className="px-2 py-1.5 font-medium">Data</th>
                          <th className="px-2 py-1.5 text-right font-medium">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data!.despesas.map((d, i) => (
                          <tr key={`${d.origem}-${d.referencia}-${i}`} className="border-b border-border/50">
                            <td className="px-2 py-1.5 font-medium">{d.origem}</td>
                            <td className="max-w-[20rem] whitespace-normal break-words px-2 py-1.5">
                              {d.descricao}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground">{d.referencia}</td>
                            <td className="px-2 py-1.5 tabular-nums">{fmtDate(d.dataRef)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {formatCurrency(d.valor)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  Receitas ({data?.receitas.length ?? 0})
                </h3>
                <p className="mb-2 text-[11px] text-muted-foreground">
                  Receita = baixas RECEBER do ERP (moto e OS, inclusive multa recebida)
                </p>
                {(data?.receitas.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma receita encontrada.</p>
                ) : (
                  <div className="max-h-72 overflow-auto rounded-md border border-border/80">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 z-[1]">
                        <tr className="border-b border-border bg-muted/80 text-left text-muted-foreground backdrop-blur-sm">
                          <th className="px-2 py-1.5 font-medium">Origem</th>
                          <th className="px-2 py-1.5 font-medium">Produto / título</th>
                          <th className="px-2 py-1.5 text-right font-medium">Valor</th>
                          <th className="px-2 py-1.5 text-right font-medium">Pago</th>
                          <th className="px-2 py-1.5 font-medium">Status</th>
                          <th className="px-2 py-1.5 font-medium">Pagamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data!.receitas.map((r, i) => (
                          <tr key={`${r.titulo}-${i}`} className="border-b border-border/50">
                            <td className="px-2 py-1.5">{r.cliente}</td>
                            <td className="px-2 py-1.5">
                              <div className="font-medium">{r.produto}</div>
                              <div className="text-muted-foreground">
                                {r.titulo}
                                {r.descricao && r.descricao !== '—' ? ` · ${r.descricao}` : ''}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {formatCurrency(r.valor)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {formatCurrency(r.valorPago)}
                            </td>
                            <td className="px-2 py-1.5">{r.status}</td>
                            <td className="px-2 py-1.5 tabular-nums">
                              {fmtDate(r.ultimoPagamento ?? r.vencimento)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
